import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';
import { toast } from 'sonner';
import { Clock, Phone, Trash2, AlertCircle } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'caller') {
      navigate('/dashboard');
      return;
    }
    fetchReminders();
  }, [user, navigate]);

  const fetchReminders = async () => {
    try {
      const response = await api.getReminders();
      setReminders(response.data);
    } catch (error) {
      toast.error('Failed to fetch reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      await api.deleteReminder(reminderId);
      toast.success('Reminder deleted successfully');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleCallLead = async (leadId) => {
    try {
      const response = await api.getLead(leadId);
      // Navigate to leads page and open the lead details
      navigate('/leads', { state: { openLeadId: leadId } });
    } catch (error) {
      toast.error('Failed to open lead');
    }
  };

  const isOverdue = (reminderTime) => {
    return new Date(reminderTime) < new Date();
  };

  const getUpcomingReminders = () => {
    return reminders.filter(r => !isOverdue(r.reminder_time) && !r.is_notified);
  };

  const getOverdueReminders = () => {
    return reminders.filter(r => isOverdue(r.reminder_time) && !r.is_notified);
  };

  const getCompletedReminders = () => {
    return reminders.filter(r => r.is_notified);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600 font-body">Loading reminders...</div>
      </div>
    );
  }

  const upcomingReminders = getUpcomingReminders();
  const overdueReminders = getOverdueReminders();
  const completedReminders = getCompletedReminders();

  const ReminderCard = ({ reminder, isOverdue = false }) => (
    <div
      data-testid={`reminder-${reminder.id}`}
      className={`bg-white rounded-lg border-2 p-4 hover:shadow-md transition-shadow ${
        isOverdue ? 'border-red-300 bg-red-50' : 'border-slate-200'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {isOverdue && <AlertCircle size={20} className="text-red-600" />}
            <h3 className="font-semibold text-slate-900 font-body">{reminder.lead_name}</h3>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-slate-600 font-mono mb-2">
            <Clock size={16} />
            <span>{formatDate(reminder.reminder_time)}</span>
          </div>

          {reminder.note && (
            <p className="text-sm text-slate-700 font-body mb-3">{reminder.note}</p>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleCallLead(reminder.lead_id)}
              data-testid={`call-lead-${reminder.id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-body"
            >
              <Phone size={14} />
              <span>Open Lead</span>
            </button>

            <button
              onClick={() => handleDeleteReminder(reminder.id)}
              data-testid={`delete-reminder-${reminder.id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors text-sm font-body"
            >
              <Trash2 size={14} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold font-headings uppercase tracking-tight text-slate-900">
          My Reminders
        </h1>
        <p className="text-slate-600 font-body mt-1">
          Manage your follow-up reminders for leads
        </p>
      </div>

      {/* Overdue Reminders */}
      {overdueReminders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={24} className="text-red-600" />
            <h2 className="text-xl font-semibold font-headings text-red-600">
              Overdue ({overdueReminders.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {overdueReminders.map((reminder) => (
              <ReminderCard key={reminder.id} reminder={reminder} isOverdue={true} />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Reminders */}
      {upcomingReminders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={24} className="text-blue-600" />
            <h2 className="text-xl font-semibold font-headings text-slate-900">
              Upcoming ({upcomingReminders.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingReminders.map((reminder) => (
              <ReminderCard key={reminder.id} reminder={reminder} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Reminders */}
      {completedReminders.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={24} className="text-slate-400" />
            <h2 className="text-xl font-semibold font-headings text-slate-600">
              Completed ({completedReminders.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedReminders.map((reminder) => (
              <ReminderCard key={reminder.id} reminder={reminder} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {reminders.length === 0 && (
        <div className="text-center py-12">
          <Clock size={64} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-600 font-body text-lg">No reminders set yet</p>
          <p className="text-slate-500 font-body text-sm mt-2">
            Go to Leads and set reminders for follow-ups
          </p>
        </div>
      )}
    </div>
  );
};

export default Reminders;
